import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DropdownProps, DropdownItem, DEFAULT_DROPDOWN_CONFIG } from './config';

const Dropdown: React.FC<DropdownProps> = ({
  items,
  value,
  onChange,
  placeholder = DEFAULT_DROPDOWN_CONFIG.placeholder,
  label,
  disabled = DEFAULT_DROPDOWN_CONFIG.disabled,
  searchable = DEFAULT_DROPDOWN_CONFIG.searchable,
  multiSelect = DEFAULT_DROPDOWN_CONFIG.multiSelect,
  className = '',
  error = false,
  errorMessage,
  required = DEFAULT_DROPDOWN_CONFIG.required,
  hideArrow = false,
  autoOpen = false,
  renderItem,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [alignRight, setAlignRight] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemsContainerRef = useRef<HTMLDivElement>(null);

  // Auto-open dropdown if autoOpen prop is true
  useEffect(() => {
    if (autoOpen && !disabled) {
      setIsOpen(true);
    }
  }, [autoOpen, disabled]);

  // Check dropdown position and adjust alignment
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const dropdownWidth = 300; // max-width from CSS
      
      // If dropdown would overflow the right edge, align it to the right
      if (rect.left + dropdownWidth > viewportWidth - 20) { // 20px buffer
        setAlignRight(true);
      } else {
        setAlignRight(false);
      }
    }
  }, [isOpen]);

  // Handle multi-select value normalization
  const selectedValues = multiSelect 
    ? (Array.isArray(value) ? value : (value ? [value] : []))
    : (value ? [value] : []);

  // Find the selected items
  const selectedItems = items.filter(item => 
    multiSelect 
      ? selectedValues.includes(item.value)
      : item.value === value
  );

  // Filter items based on search term
  const filteredItems = searchable
    ? items.filter(item =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : items;

  // Sort items: selected items first (for multi-select), then others
  const sortedItems = multiSelect 
    ? [
        ...filteredItems.filter(item => selectedValues.includes(item.value)),
        ...filteredItems.filter(item => !selectedValues.includes(item.value))
      ]
    : filteredItems;

  // Reset highlighted index when dropdown opens or items change
  useEffect(() => {
    if (isOpen) {
      // Find the index of the currently selected item, or start at 0
      const selectedIndex = sortedItems.findIndex(item => 
        multiSelect ? selectedValues.includes(item.value) : item.value === value
      );
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    } else {
      setHighlightedIndex(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sortedItems.length, searchTerm]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && itemsContainerRef.current) {
      const container = itemsContainerRef.current;
      const highlightedElement = container.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isOpen) {
      // Open dropdown on arrow down or enter
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const nextIndex = prev + 1;
          // Skip disabled items
          let targetIndex = nextIndex;
          while (targetIndex < sortedItems.length && sortedItems[targetIndex]?.disabled) {
            targetIndex++;
          }
          return targetIndex < sortedItems.length ? targetIndex : prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const nextIndex = prev - 1;
          // Skip disabled items
          let targetIndex = nextIndex;
          while (targetIndex >= 0 && sortedItems[targetIndex]?.disabled) {
            targetIndex--;
          }
          return targetIndex >= 0 ? targetIndex : prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < sortedItems.length) {
          handleSelect(sortedItems[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'Tab':
        // Allow tab to close dropdown and move focus
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, highlightedIndex, sortedItems]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle item selection
  const handleSelect = (item: DropdownItem) => {
    if (!item.disabled) {
      if (multiSelect) {
        const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
        const isSelected = currentValues.includes(item.value);
        
        if (isSelected) {
          // Deselect item
          const newValues = currentValues.filter(val => val !== item.value);
          onChange(newValues);
        } else {
          // Select item
          onChange([...currentValues, item.value]);
        }
        // Keep dropdown open for multi-select
      } else {
        // Single select behavior
        onChange(item.value);
        setIsOpen(false);
        setSearchTerm('');
      }
    }
  };

  // Handle dropdown toggle
  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div 
      className={`dropdown-container ${className} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
      ref={dropdownRef}
    >
      {label && (
        <label className="dropdown-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}
      
      <div 
        className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="dropdown-listbox"
        aria-activedescendant={highlightedIndex >= 0 ? `dropdown-item-${highlightedIndex}` : undefined}
      >
        <div className="selected-value">
          {multiSelect
            ? (selectedItems.length === 0
                ? placeholder
                : selectedItems.length <= 2
                  ? selectedItems.map((i) => i.label).join(', ')
                  : `${selectedItems.length} selected`)
            : (selectedItems.length > 0 ? selectedItems[0].label : placeholder)
          }
        </div>
        <div className="dropdown-arrow">
          {!hideArrow && (
            <svg 
              width="12" 
              height="8" 
              viewBox="0 0 12 8" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className={isOpen ? 'rotated' : ''}
            >
              <path 
                d="M1 1.5L6 6.5L11 1.5" 
                stroke="currentColor" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      </div>

      {isOpen && (
        <div className={`dropdown-menu ${alignRight ? 'align-right' : ''}`}>
          {searchable && (
            <div className="search-container">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                placeholder="Search..."
                className="search-input dropdown-search-input"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          )}

          <div className="items-container" ref={itemsContainerRef} role="listbox" id="dropdown-listbox">
            {sortedItems.length > 0 ? (
              sortedItems.map((item, index) => (
                <div
                  key={item.id}
                  id={`dropdown-item-${index}`}
                  className={`dropdown-item ${item.disabled ? 'disabled' : ''} ${selectedValues.includes(item.value) ? 'selected' : ''} ${highlightedIndex === index ? 'highlighted' : ''}`}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  role="option"
                  aria-selected={selectedValues.includes(item.value)}
                >
                  {renderItem ? renderItem(item) : item.label}
                </div>
              ))
            ) : (
              <div className="no-results">No results found</div>
            )}
          </div>
        </div>
      )}

      {error && errorMessage && (
        <div className="error-message">{errorMessage}</div>
      )}
    </div>
  );
};

export default Dropdown; 